import React, { useState, useRef, useEffect } from 'react';
import { Outlet } from 'react-router';
import '../../questionsSearch.scss';
import { NavLink } from 'react-router-dom';
import QuestionsResults from './components/questionResults/questionsResults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePlus, faSquareMinus } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-duotone-svg-icons';
import { useLazyGetPrimaryCourseListQuery } from '../../../../features/authentication/questionSearchApiSlice';
import { useQuestionSearchContext } from './context';
import ActivityTypes from '../../../../enums/activityTypes';

const items = [
  {
    id: 1,
    name: 'Castle Questions',
    path: 'questions',
    guideLines: <div className='guidelines'>
      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that questions are added and removed from assignment upon clicking Add/Remove. To return to the assignment, click Add Questions.</span>
      </div>
      <ul>
        <li>Do not use any quotes in your search and please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of questions matching will update when finished.</li>
        <li>Enter ONE keyword in the search box to find questions containing that word.</li>
        <li>Enter multiple keywords separated by OR to find questions containing any of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality OR inequalities will find questions with either of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter multiple keywords separated by AND to find questions containing all of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality AND inequalities will find questions with both of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter a specific phrase with words separated by spaces to find questions containing that specific phrase. The keywords have to be adjacent.
          <ul>
            <li>
              For example, solve the equation will find questions with that specific phrase.
            </li>
          </ul>
        </li>
      </ul>
    </div>
  },
  {
    id: 2,
    name: 'Exams/Assignments',
    path: `exams_assignments/${ActivityTypes.Assignment}`,
    guideLines: <div className='guidelines'>
      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that questions are added and removed from assignment upon clicking Add/Remove. To return to the assignment, click Add Questions.</span>
      </div>
      <ul>
        <li>Use this tab when searching for keywords in titles of exams/assignments.</li>
        <li>Do not use any quotes in your search and please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of questions matching will update when finished.</li>
        <li>Enter ONE keyword in the search box to find exam/assignment titles containing that word.</li>
        <li>Enter multiple keywords separated by OR to find exam/assignment titles containing any of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality OR inequalities will find exam/assignment titles with either of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter multiple keywords separated by AND to find exam/assignment containing all of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality AND inequalities will find exam/assignment with both of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter a specific phrase with words separated by spaces to find exam/assignment containing that specific phrase. The keywords have to be adjacent.
          <ul>
            <li>
              For example, common core will find exam/assignment with that specific phrase.
            </li>
          </ul>
        </li>
        <li className='fw-500'>For performance reasons the search is limited to the first 100 assignments found. Use more keywords to narrow your search.</li>
      </ul>
    </div>
  },
  {
    id: 3,
    name: 'Standards',
    path: 'standards',
    guideLines: <div className='guidelines'>

      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that questions are added and removed from assignment upon clicking Add/Remove. To return to the assignment, click Add Questions.</span>
      </div>
      <ul>
        <li>Enter the EXACT standard number you wish to find question for. Once matching standards are found a dropdown will be displayed. Select the standard you want from the dropdown.
          <ul>
            <li>
              For example, 3.G.1
            </li>
          </ul>
        </li>
        <li>Please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of questions matching will update when finished.</li>
      </ul>
    </div>
  },
  {
    id: 4,
    name: 'Question ID',
    path: 'questionId',
    comingSoon: false,
    guideLines: <div className='guidelines'>

      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that questions are added and removed from assignment upon clicking Add/Remove. To return to the assignment, click Add Questions.</span>
      </div>
      <ul>
        <li>Enter the EXACT number for the Question ID you wish to find. Do not use quotes or letters.</li>
        <li>If there is no question that matches the number you entered, a message will be displayed to let you know there was no match for that ID.</li>
      </ul>
    </div>
  },
  {
    id: 4,
    name: 'My Content',
    path: 'mycontent',
    comingSoon: false,
    guideLines: <div className='guidelines'>
      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that questions are added and removed from assignment upon clicking Add/Remove. To return to the assignment, click Add Questions.</span>
      </div>
      <ul>
        <li>Do not use any quotes in your search and please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of questions matching will update when finished.</li>
        <li>Enter ONE keyword in the search box to find questions containing that word.</li>
        <li>Enter multiple keywords separated by OR to find questions containing any of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality OR inequalities will find questions with either of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter multiple keywords separated by AND to find questions containing all of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality AND inequalities will find questions with both of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter a specific phrase with words separated by spaces to find questions containing that specific phrase. The keywords have to be adjacent.
          <ul>
            <li>
              For example, solve the equation will find questions with that specific phrase.
            </li>
          </ul>
        </li>
      </ul>
    </div>
  }
];

const Index = () => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [selectedItem, setSelectedItem] = useState(items[0]);
  const questionsResultsRef = useRef();
  const [getPrimaryCourses, primaryCourseData] = useLazyGetPrimaryCourseListQuery();
  const questionSearchContext = useQuestionSearchContext();

  useEffect(() => {
    try {
      getPrimaryCourses().unwrap;
    } catch (ex) {
      console.log(ex);
    }
  }, []);

  useEffect(() => {
    questionsResultsRef.current?.endCastleSearch();
    questionSearchContext.setQuestionsTabBasicCourse([]);
    questionSearchContext.setQuestionsTabBasicGradeIds([]);
  }, [selectedItem]);
  
  useEffect(() => {

    if (!primaryCourseData.isSuccess) return;

    var advMathFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 101)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var advScienceFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 102)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var advSocialStudyFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 103)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var advEnglishFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 104)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var advForeignLanFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 105)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var advOtherFilters = primaryCourseData.data.courses.find(c => c.mainCourse.id === 0)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];
    var examTabGrades = questionSearchContext.gradeFilterOptions.map((item) => ({ ...item, isChecked: false }));
    var examTabCourses = primaryCourseData.data.courses.filter(c => c.mainCourse.id > 0).map(c => ({ name: c.mainCourse.name, id: c.mainCourse.id, isChecked: false }));
    var examTabStates = primaryCourseData.data.examStates.map(s => ({ name: s.name, id: s.id, isChecked: false }));
    var examTabYears = primaryCourseData.data.examYears.map(s => ({ name: s.name, id: s.id, isChecked: false }));

    questionSearchContext.setQuestionsTabAdvMathCourses(advMathFilters);
    questionSearchContext.setQuestionsTabAdvSocialStudiesCourses(advSocialStudyFilters);
    questionSearchContext.setQuestionsTabAdvScienceCourses(advScienceFilters);
    questionSearchContext.setQuestionsTabAdvEnglishCourses(advEnglishFilters);
    questionSearchContext.setQuestionsTabAdvForeignLangCourses(advForeignLanFilters);
    questionSearchContext.setQuestionsTabAdvOtherCourses(advOtherFilters);
    questionSearchContext.setExamsTabSelectedGrades(examTabGrades);
    questionSearchContext.setExamsTabSelectedCourses(examTabCourses);
    questionSearchContext.setExamsTabSelectedStates(examTabStates);
    questionSearchContext.setExamsTabSelectedExamYears(examTabYears);

  }, [primaryCourseData]);

  const handleGetCourseFilters = () => {
    getPrimaryCourses().unwrap;
  };

  const handleCastleSearch = (searchRequest) => {
    questionsResultsRef.current?.initializeCastleSearch(searchRequest);
  };

  const handleStandardsSearch = (searchRequest) => {
    questionsResultsRef.current?.initalizeStandardsSearch(searchRequest);
  };

  const handleTeacherSearch = (searchRequest) => {
    questionsResultsRef.current?.initializeTeacherSearch(searchRequest);
  };

  const handleExamSearch = (searchRequest) => {
    questionsResultsRef.current?.initializeExamSearch(searchRequest);
  };

  const handleQuestionIdSearch = (searchRequest) => {
    questionsResultsRef.current?.initializeQuestionIdSearch(searchRequest);
  };

  const handleTeacherAssignmentSearch = (assignmentId) => {
    questionsResultsRef.current?.initializeTeacherAssignmentSearch(assignmentId);
  };

  const handlePublicAssignmentSearch = (assignmentId) => {
    questionsResultsRef.current?.initializePublicAssignmentSearch(assignmentId);
  };

  const endCastleSearch = () => {
    questionsResultsRef.current?.endCastleSearch();
  };

  return (

    <div className="d-flex flex-column">
      <div className="d-flex flex-column whiteCard">
        <div className='d-flex flex-row justify-content-between'>
          <div className='castleQuestionsList mb-4'>
            {items.map((item, index) => (
              <NavLink id={item.name} key={`searchType${index}`} className="castleQuestionTypeBtn" to={item.path} onClick={() => setSelectedItem(item)} end>
                <div className='iconBox'>
                </div>
                {item.name}
              </NavLink>
            ))}
          </div>

          <div className='d-flex flex-row align-items-center justify-content-center fw-500' style={{ color: '#09BCF3', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowGuidelines(!showGuidelines)} onBlur={() => setSelectedItem(items[0])}>
            <span>Guidelines</span>
            <FontAwesomeIcon style={{ fontSize: '18px', marginLeft: '5px' }} icon={showGuidelines ? faSquareMinus : faSquarePlus} />
          </div>
        </div>

        {showGuidelines && selectedItem.guideLines}

        <Outlet context={{
          intializeCastleSearch: handleCastleSearch,
          intializeTeacherSearch: handleTeacherSearch,
          initalizeStandardsSearch: handleStandardsSearch,
          initializeExamSearch: handleExamSearch,
          initializeTeacherAssignmentSearch: handleTeacherAssignmentSearch,
          initializePublicAssignmentSearch: handlePublicAssignmentSearch,
          courseFilters: primaryCourseData,
          fetchCourseFilters: handleGetCourseFilters,
          initializeQuestionIdSearch: handleQuestionIdSearch,
          endCastleSearch: endCastleSearch
        }} />
      </div>

      <QuestionsResults ref={questionsResultsRef} />
    </div>

  );
};

export default Index;
